<script lang="ts">
	import AbleyButton from './AbleyButton.svelte';
	// Aliased: `MenuItem` is also the item type exported below.
	import MenuRow from './MenuItem.svelte';
	import IconChevronDown from '#lib/assets/chevron-down.svg?raw';
	import IconChevronRight from '#lib/assets/chevron-right.svg?raw';
	import IconDownload from '#lib/assets/arrow-down-tray.svg?raw';
	import IconLink from '#lib/assets/arrow-top-right-on-square.svg?raw';

	export type MenuLinkItem = {
		label: string;
		href?: string;
		target?: string;
		disabled?: boolean;
		// Non-clickable "you are here" entry, e.g. the active app or data version.
		active?: boolean;
		type?: 'download' | 'link';
		// In-app action instead of navigating (e.g. picking a data version); ignored when `href` is set.
		onclick?: () => void;
	};

	export type MenuItem = {
		link?: MenuLinkItem;
		submenu?: { title: string; links: MenuLinkItem[] };
		// Non-interactive label rendered in the dropdown (e.g. an app-version
		// line in the Help menu). No anchor, no button — just text.
		info?: { label: string };
		// Triggers an in-app action (e.g. opening the support modal) rather than
		// navigating. The parent maps `action` to behaviour via `onaction`.
		button?: { label: string; action: string; disabled?: boolean };
	};

	interface Props {
		items: MenuItem[];
		title: string;
		mobile?: boolean;
		onaction?: (action: string) => void;
		/** Fired on hover/focus of a `button` item — lets the parent warm work early. */
		onactionhover?: (action: string) => void;
	}

	const { items, title, mobile = false, onaction, onactionhover }: Props = $props();

	let open = $state(false);

	const iconFor = (item: MenuLinkItem) =>
		item.type === 'download' ? IconDownload : item.type === 'link' ? IconLink : undefined;
</script>

{#snippet menuLink(item: MenuLinkItem)}
	{#if item.disabled}
		<MenuRow disabled icon={iconFor(item)}>{item.label}</MenuRow>
		<span class="reason">Not included in your current subscription</span>
	{:else}
		<!-- `item.href` is an external URL, mailto: or static asset from app config, never a route. -->
		<MenuRow
			href={item.href}
			target={item.target ?? '_blank'}
			current={item.active}
			icon={iconFor(item)}
			onclick={() => {
				open = false;
				item.onclick?.();
			}}>
			{item.label}
		</MenuRow>
	{/if}
{/snippet}

<div class="hasDropdown" class:open class:mobileNav={mobile}>
	<AbleyButton
		variant="text"
		aria-expanded={mobile ? open : undefined}
		onclick={() => {
			if (mobile) open = !open;
		}}>
		{title}{@html IconChevronDown}
	</AbleyButton>
	<ul class="dropdown">
		{#each items as item, i (i)}
			{#if item.link}
				<li>{@render menuLink(item.link)}</li>
			{:else if item.info}
				<li class="infoItem">{item.info.label}</li>
			{:else if item.submenu}
				<li class="hasSubmenu" class:open>
					<MenuRow icon={mobile ? undefined : IconChevronRight}>{item.submenu.title}</MenuRow>
					<ul class="submenu">
						{#each item.submenu.links as subItem, j (j)}
							<li>{@render menuLink(subItem)}</li>
						{/each}
					</ul>
				</li>
			{:else if item.button}
				{@const button = item.button}
				<!-- Hover/focus warms the action's work before the click (e.g. preloads
				     the support embed); the click triggers it. -->
				<li
					onpointerenter={() => onactionhover?.(button.action)}
					onfocusin={() => onactionhover?.(button.action)}>
					<MenuRow
						disabled={button.disabled}
						onclick={() => {
							// Collapse the mobile accordion before the action opens its overlay.
							open = false;
							onaction?.(button.action);
						}}>
						{button.label}
					</MenuRow>
				</li>
			{/if}
		{/each}
	</ul>
</div>

<style>
	.hasDropdown {
		display: inline-block;
		position: relative;
	}

	.dropdown {
		position: absolute;
		top: 100%;
		left: 0;
		visibility: hidden;
		z-index: var(--z-menu);
		margin: 0;
		box-shadow: var(--shadow-lg);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		background-color: var(--surface-raised);
		padding: var(--space-2) 0;
		/* Absolutely positioned inside the narrow trigger, so shrink-to-fit would squeeze the row icons. */
		width: max-content;
		min-width: 150px;
		list-style: none;
	}

	/* Desktop opens on hover/focus-within by choice, not click or `popover` (contact-support specs hover "Help"). */
	.hasDropdown:hover .dropdown,
	.hasDropdown:focus-within .dropdown,
	.hasDropdown.open .dropdown {
		visibility: visible;
	}

	.dropdown li {
		position: relative;
	}

	/* Plain-text label item (e.g. app version line in Help). */
	.dropdown li.infoItem {
		padding: var(--space-2) var(--space-3);
		color: var(--text-muted);
		font-size: var(--font-size-sm);
		white-space: nowrap;
	}

	.reason {
		display: block;
		padding: 0 var(--space-3) var(--space-2);
		color: var(--text-muted);
		font-size: var(--font-size-xs);
		white-space: nowrap;
	}

	/* Level 2: opens to the right */
	.hasSubmenu .submenu {
		display: none;
		position: absolute;
		top: 0;
		left: 100%;
		z-index: var(--z-menu);
		margin: 0;
		box-shadow: var(--shadow-lg);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		background-color: var(--surface-raised);
		padding: 0;
		width: max-content;
		min-width: 150px;
		list-style: none;
	}

	.hasSubmenu:hover .submenu,
	.hasSubmenu:focus-within .submenu {
		display: block;
	}

	/* Mobile: inline accordion */
	.mobileNav.hasDropdown {
		display: block;
		width: 100%;
	}

	.mobileNav.hasDropdown > :global(button) {
		justify-content: flex-start;
		padding-inline: var(--space-4);
		width: 100%;
	}

	.mobileNav .dropdown {
		display: none;
		position: static;
		visibility: visible;
		margin-left: var(--space-5);
		box-shadow: none;
		border: none;
		border-left: 4px solid var(--border-color);
		border-radius: 0;
		background: transparent;
		padding: 0;
		min-width: unset;
	}

	.mobileNav.open .dropdown {
		display: block;
	}

	.mobileNav .hasSubmenu .submenu {
		display: none;
		position: static;
		box-shadow: none;
		margin-left: var(--space-6);
		border: none;
		padding: 0;
	}

	.mobileNav .hasSubmenu:hover .submenu,
	.mobileNav .hasSubmenu.open .submenu,
	.mobileNav .hasSubmenu:focus-within .submenu {
		display: block;
	}
</style>
